import { useEffect, useState } from "react";
import { Col, Row } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { ModalAtom, TextFieldAtom } from "../../../components/atoms/atom";

/**
 * Modal for editing a project-only company
 * Only project-specific companies (not from CAPMO) can be edited
 */
function EditProjectCompanyModal({ show, onClose, onSubmit, loading = false, companyData }) {
    const { t } = useTranslation();

    const [formData, setFormData] = useState({
        name: "",
        addressStreet: "",
        addressZipCode: "",
        addressCity: "",
        addressCountry: "",
        taxId: "",
        phoneNumber: "",
    });

    const [errors, setErrors] = useState({});

    // Populate form when companyData changes
    useEffect(() => {
        if (companyData) {
            setFormData({
                name: companyData.name || "",
                addressStreet: companyData.addressStreet || "",
                addressZipCode: companyData.addressZipCode || "",
                addressCity: companyData.addressCity || "",
                addressCountry: companyData.addressCountry || "",
                taxId: companyData.taxId || "",
                phoneNumber: companyData.phoneNumber || "",
            });
        }
    }, [companyData]);

    const handleChange = (field, value) => {
        setFormData((prev) => ({ ...prev, [field]: value }));
        // Clear error for this field
        if (errors[field]) {
            setErrors((prev) => ({ ...prev, [field]: "" }));
        }
    };

    const validateForm = () => {
        const newErrors = {};

        if (!formData.name || formData.name.trim() === "") {
            newErrors.name = "projects.companies.error-company-name-required";
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = () => {
        if (!validateForm()) {
            return;
        }

        // Clean up empty string values to null
        const cleanedData = Object.entries(formData).reduce((acc, [key, value]) => {
            acc[key] = value && value.trim() !== "" ? value.trim() : null;
            return acc;
        }, {});

        onSubmit(cleanedData);
    };

    const handleClose = () => {
        setErrors({});
        onClose();
    };

    return (
        <ModalAtom
            show={show}
            handleClose={handleClose}
            title={t("projects.companies.edit-project-company")}
            saveText={loading ? "common.saving" : "common.save"}
            handleSubmit={handleSubmit}
            customClassName="edit-project-company-modal"
            size="lg"
            body={
                <div className="px-3">
                    <Row>
                        <Col md={12}>
                            <TextFieldAtom
                                label={t("companies.company-name")}
                                value={formData.name}
                                onChange={(e) => handleChange("name", e.target.value)}
                                error={errors.name}
                                required
                                placeholder={t("companies.enter-company-name")}
                            />
                        </Col>
                    </Row>

                    <Row>
                        <Col md={12}>
                            <TextFieldAtom
                                label={t("companies.address-street")}
                                value={formData.addressStreet}
                                onChange={(e) => handleChange("addressStreet", e.target.value)}
                                placeholder={t("companies.enter-street-address")}
                            />
                        </Col>
                    </Row>

                    <Row>
                        <Col md={4}>
                            <TextFieldAtom
                                label={t("companies.zip-code")}
                                value={formData.addressZipCode}
                                onChange={(e) =>
                                    handleChange("addressZipCode", e.target.value)
                                }
                                placeholder={t("companies.enter-zip-code")}
                            />
                        </Col>
                        <Col md={8}>
                            <TextFieldAtom
                                label={t("companies.city")}
                                value={formData.addressCity}
                                onChange={(e) => handleChange("addressCity", e.target.value)}
                                placeholder={t("companies.enter-city")}
                            />
                        </Col>
                    </Row>

                    <Row>
                        <Col md={6}>
                            <TextFieldAtom
                                label={t("companies.country")}
                                value={formData.addressCountry}
                                onChange={(e) =>
                                    handleChange("addressCountry", e.target.value)
                                }
                                placeholder={t("companies.enter-country")}
                            />
                        </Col>
                        <Col md={6}>
                            <TextFieldAtom
                                label={t("companies.tax-id")}
                                value={formData.taxId}
                                onChange={(e) => handleChange("taxId", e.target.value)}
                                placeholder={t("companies.enter-tax-id")}
                            />
                        </Col>
                    </Row>

                    <Row>
                        <Col md={12}>
                            <TextFieldAtom
                                label={t("companies.phone-number")}
                                value={formData.phoneNumber}
                                onChange={(e) => handleChange("phoneNumber", e.target.value)}
                                placeholder={t("companies.enter-phone-number")}
                            />
                        </Col>
                    </Row>
                </div>
            }
        />
    );
}

export default EditProjectCompanyModal;
